<template>
  <div class="edit-traveller">
    <button v-if="!context" class="btn btn-sm orange-white"
            :title="$t(title)"
            @click="onEditTraveller"
            v-b-tooltip
    >
      <fa fa-class="edit" />
    </button>

    <b-modal id="traveller-edit"
             v-model="showModal"
             :cancel-disabled="true"
             :ok-disabled="true"
             :title="$t('traveller.edit.title')"
             :hide-footer="true"
    >
      <div>
        <form-base type="traveller_edit"
                   :form-data="travellerEditData"
                   :default-submit-button="false"
                   :callbacks="{ editTraveller: onConfirmEditTraveller }"
                   invoked-submit-button-name="editTraveller"
                   @error="setErrorVisible"
                   :class="nameRoute"
        >
          <b-tabs>
            <b-tab :title="$t('_general')">
              <radio-input label="_gender"
                           name="gender"
                           :options="genderOptions"
                           v-model="travellerEditData.gender"
                           ref="gender"
              />

              <text-input label="_first_name"
                          name="first-name"
                          v-model="travellerEditData.first_name"
                          :required="false"
                          ref="firstName"
              />

              <text-input label="_last_name"
                          name="last-name"
                          v-model="travellerEditData.last_name"
                          ref="lastName"
              />

              <text-input label="_phone_number"
                          name="phone"
                          v-model="travellerEditData.mobile_phone"
                          ref="mobilePhone"
              />

              <text-input label="_email"
                          name="email"
                          v-model="travellerEditData.email"
                          type="email"
                          ref="email"
              />

              <text-input label="_email2"
                          name="email2"
                          v-model="travellerEditData.email2"
                          type="email"
                          ref="email2"
                          :required="false"
              />
            </b-tab>

            <b-tab :title="$t('_custom_fields')"
                   v-if="travellerEditData.companyInputFields.length"
            >
              <custom-input-controller :fields="travellerEditData.companyInputFields"
                                       @input="onInputCustomFields"
                                       :verticalClass="true" no-required-field
              />
            </b-tab>

            <div v-if="submitted" class="col-12">
              <b-alert v-if="success" variant="success" show>
                <span>{{ $t('traveller.edit.alert.success') }}</span>
              </b-alert>

              <b-alert v-else-if="errors.length" variant="danger" class="col-md-12"
                       dismissible show
              >
                <ul>
                  <li v-for="error in errors" :key="error.id" class="error">
                    <span class="label">
                      {{ $t(labels[error.field]) | capitalise }} :
                    </span>

                    {{ error.msg }}
                  </li>
                </ul>
              </b-alert>
            </div>

            <b-row class="justify-content-center">
              <submit-button v-if="!submitted || !success" class="btn"
                             text="_validate" name="editTraveller"
              />
           </b-row>
          </b-tabs>
        </form-base>
      </div>

    </b-modal>
  </div>
</template>

<script>
import CommonApi from '@/api/commonApi'
import formBase from '@/components/frontoffice/form/base'
import bModal from 'bootstrap-vue/es/components/modal/modal'
import customInputController from '@/components/frontoffice/form/customInput/controller'
import submitButton from '@/components/frontoffice/form/baseInput/submit'
import fa from '@/components/global/icon/fontAwesome'
import textInput from '@/components/frontoffice/form/baseInput/text'
import radioInput from '@/components/frontoffice/form/baseInput/radio'

export default {
  components: {
    formBase,
    bModal,
    customInputController,
    submitButton,
    fa,
    textInput,
    radioInput
  },

  props: {
    traveller: Object,
    companyInputFields: Array,
    title: {
      type: String,
      default: '_edit'
    }
  },

  data () {
    return {
      context: this.$route.matched[0].name === 'admin',
      nameRoute: this.$route.matched[0].name,
      showModal: false,
      commonApi: new CommonApi(),
      genderOptions: [
        { value: 'male', label: '_male', selected: false },
        { value: 'female', label: '_female', selected: false }
      ],
      travellerEditData: Object,
      submitted: false,
      success: false,
      errors: [],
      labels: {
        gender: '_gender',
        firstName: '_first_name',
        lastName: '_last_name',
        landline: '_landline',
        mobilePhone: '_mobile_phone',
        phones: '_phone_number',
        email: '_email',
        email2: '_email2',
        isManager: '_is_manager',
        enabled: '_enabled'
      }
    }
  },

  created () {
    this.travellerEditData = this.traveller
    this.travellerEditData.companyInputFields = this.clone(this.companyInputFields)

    for (let gender of this.genderOptions) {
      gender.selected = this.isGenderSelected(gender.value)
    }

    // init custom fields
    for (let tif of this.travellerEditData.traveller_input_fields) {
      let cif = this.travellerEditData.companyInputFields.find(x => x.id === tif.booking_input_field.id)

      if (tif.booking_input_field.type === 40) { // Boolean
        for (let option of cif.options) {
          if (option.value === tif.value) {
            option.selected = true
          }
        }
      } else {
        cif.value = tif.value
      }
    }
  },

  methods: {
    clone: x => JSON.parse(JSON.stringify(x)),

    setErrorVisible (errors) {
      this.submitted = true
      this.success = false
      this.errors = errors
    },

    onEditTraveller () {
      this.showModal = !this.showModal
    },

    onConfirmEditTraveller (data) {
      if (data) {
        this.success = true
        this.submitted = true

        setTimeout(() => {
          this.showModal = false
          this.submitted = false
          this.$emit('input', data)
        }, 3000)
      } else {
        this.success = false
      }
    },

    onInputCustomFields (newVal) {
      if (newVal.value === undefined) return
      let inputFieldId = newVal.id
      let tif = this.travellerEditData.traveller_input_fields.find(x => x.booking_input_field.id === inputFieldId)

      if (tif) {
        tif.value = newVal.value
      } else {
        this.travellerEditData.traveller_input_fields.push({
          value: newVal.value,
          booking_input_field: { id: inputFieldId }
        })
      }
    },

    isGenderSelected (value) {
      return this.convertGenderFormat(this.travellerEditData.gender) === value
    },

    convertGenderFormat (value) {
      let ret = ''

      switch (value) {
        case 'M.':
        case 'm':
        case 'male':
        case 'M':
          ret = 'male'
          break
        case 'Mme':
        case 'f':
        case 'female':
        case 'F':
          ret = 'female'
          break
      }

      return ret
    }
  }
}
</script>
<style scoped>
.admin form .custom-input:not(.dates){
  border-bottom: none;
  margin-right: 1rem;
}

.admin form .custom-input {
  border-bottom: none;
  margin-top: 0;
  height: 90px;
}

.admin form .refs-input{
  padding-left: 0;
  padding-right: 0;
}

.admin .input-inner{
  border-bottom: none;
}
</style>
